<template>
  <div class="sf-categories" :class="`sf-categories--${style}`">
    <h5 v-if="section.settings.title" class="sf-heading sf-section-title px-4 sm:px-0">{{ section.settings.title }}</h5>

    <!-- Carrossel de ícones (padrão) -->
    <template v-if="style === 'carousel'">
      <div v-if="categories?.length" class="home__categories-list sm:pb-8 sm:pt-6">
        <swiper
          :slides-per-view="9"
          :space-between="50"
          :pagination="true"
          :modules="modules"
          :breakpoints="carouselBreakpoints"
          :free-mode="true"
          class="pb-10"
        >
          <swiper-slide v-for="category in categories" :key="category.id">
            <RouterLink :to="{ name: 'categories.show', params: { id: category.id } }">
              <div class="transition-transform transform-gpu hover:scale-110 cursor-pointer home__categories-list__category items-center">
                <img class="sf-category-img" :src="getProductImage(category)" alt="" @error="($event.target as HTMLImageElement).src = '/assets/no-image.png'">
                <p class="text-center text-sm only-1-lines">{{ category.name }}</p>
              </div>
            </RouterLink>
          </swiper-slide>
        </swiper>
      </div>
      <div v-if="!categories?.length && !restaurant?.id" class="home__categories-list sm:pb-8 sm:pt-6">
        <swiper
          :slides-per-view="9"
          :space-between="50"
          :pagination="true"
          :modules="modules"
          :breakpoints="carouselBreakpoints"
          :free-mode="true"
          class="pb-10"
        >
          <swiper-slide v-for="i in [1,2,3,4,5,6,7,8,9]" :key="i">
            <button>
              <div class="cursor-pointer home__categories-list__category skeleton animate-pulse">
                <div class="img-skeleton rounded-2xl sf-skeleton w-full"></div>
                <p class="text-center mx-auto p-skeleton sf-skeleton text-sm h-4 w-full"></p>
              </div>
            </button>
          </swiper-slide>
        </swiper>
      </div>
    </template>

    <!-- Grade de categorias -->
    <template v-else-if="style === 'grid'">
      <div v-if="categories?.length" class="sf-categories__grid px-4 sm:px-0 pb-8 pt-2">
        <RouterLink v-for="category in categories" :key="category.id" :to="{ name: 'categories.show', params: { id: category.id } }" class="sf-categories__tile group">
          <div class="sf-categories__tile-img">
            <img :src="getProductImage(category)" alt="" class="transition-transform duration-300 group-hover:scale-105" @error="($event.target as HTMLImageElement).src = '/assets/no-image.png'">
          </div>
          <p class="text-center text-sm font-medium only-1-lines pt-2">{{ category.name }}</p>
        </RouterLink>
      </div>
      <div v-else-if="!restaurant?.id" class="sf-categories__grid px-4 sm:px-0 pb-8 pt-2 animate-pulse">
        <div v-for="i in [1,2,3,4,5,6,7,8]" :key="i" class="sf-categories__tile">
          <div class="sf-categories__tile-img sf-skeleton"></div>
          <p class="sf-skeleton h-4 mt-2 w-full"></p>
        </div>
      </div>
    </template>

    <!-- Chips (pílulas com o nome) -->
    <template v-else>
      <div v-if="categories?.length" class="sf-categories__chips px-4 sm:px-0 pb-8 pt-2">
        <RouterLink v-for="category in categories" :key="category.id" :to="{ name: 'categories.show', params: { id: category.id } }" class="sf-categories__chip">
          {{ category.name }}
        </RouterLink>
      </div>
      <div v-else-if="!restaurant?.id" class="sf-categories__chips px-4 sm:px-0 pb-8 pt-2 animate-pulse">
        <span v-for="i in [1,2,3,4,5,6]" :key="i" class="sf-categories__chip sf-skeleton" style="width:6rem">&nbsp;</span>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Pagination, FreeMode } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import 'swiper/css'
import 'swiper/css/pagination'
import { restaurant, categories, getProductImage } from '@/global/state/GlobalState'
import { CategoriesSection } from '@/global/state/StorefrontState'

/* global defineProps */
const props = defineProps<{ section: CategoriesSection }>()

const modules = [Pagination, FreeMode]
const style = computed(() => props.section.settings?.style ?? 'carousel')
const carouselBreakpoints = { 0: { slidesPerView: 4, spaceBetween: 20 }, 450: { slidesPerView: 5, spaceBetween: 20 }, 900: { slidesPerView: 6, spaceBetween: 40 }, 1200: { slidesPerView: 9, spaceBetween: 40 } }

const menuTextColor = computed(() => restaurant.value.restaurantDesignCustom?.menuTextColor || 'var(--sf-text)')
const menuTextHoverColor = computed(() => restaurant.value.restaurantDesignCustom?.menuTextHoverColor || 'var(--sf-primary)')
</script>

<style lang="scss">
.sf-section-title {
  font-size: 1.25rem;
  font-weight: 700;
  padding-bottom: 1rem;

  @media screen and (min-width: 768px) {
    font-size: 1.5rem;
  }
}

.sf-categories {
  .home__categories-list__category {
    display: flex;
    gap: .75rem;
    flex-direction: column;
    margin: 0;

    img, .img-skeleton {
      height: 4rem;
      width: 80px;
      object-fit: fill;
      border-radius: var(--sf-radius);
    }

    p {
      color: v-bind('menuTextColor');
    }

    &:hover p {
      color: v-bind('menuTextHoverColor');
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 1rem .75rem;

    @media screen and (min-width: 640px) {
      grid-template-columns: repeat(6, minmax(0, 1fr));
    }
    @media screen and (min-width: 1024px) {
      grid-template-columns: repeat(8, minmax(0, 1fr));
      gap: 1.5rem 1.25rem;
    }
  }

  &__tile {
    display: block;
    color: v-bind('menuTextColor');

    &:hover {
      color: v-bind('menuTextHoverColor');
    }
  }

  &__tile-img {
    aspect-ratio: 1 / 1;
    overflow: hidden;
    border-radius: var(--sf-radius);
    background: #f3f4f6;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__chips {
    display: flex;
    gap: .5rem;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__chip {
    flex-shrink: 0;
    padding: .5rem 1rem;
    font-size: .875rem;
    font-weight: 600;
    white-space: nowrap;
    border: 1px solid var(--sf-border);
    border-radius: 999px;
    background: var(--sf-card-bg);
    color: var(--sf-text);
    transition: all .2s;

    &:hover {
      background: var(--sf-primary);
      border-color: var(--sf-primary);
      color: var(--sf-on-primary);
    }
  }
}

@media screen and (max-width: 600px) {
  .home__categories-list {
    padding: 0 1rem !important;

    button {
      width: 100%
    }
  }

  .sf-categories .home__categories-list__category {
    padding: 0 !important;
    width: 100% !important;

    &.skeleton {
      padding-bottom: 0 !important;
    }

    img {
      height: 55px !important;
      width: 65px !important;
    }

    .img-skeleton {
      width: 70px
    }

    p {
      font-size: 13px
    }
  }
}
</style>
